import React from 'react';
import { Modal } from 'antd';
import { Ticket } from '../types';
import { EyeOutlined } from '@ant-design/icons';

interface Props {
	ticket: Ticket | null;
	onClose: () => void;
}

const formatDate = (dateString: string | null) => {
	if (!dateString) {
		return '';
	}
	return new Date(dateString).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

export const ResponseDetailsModal: React.FC<Props> = ({ ticket, onClose }) => {
	if (!ticket) {
		return null;
	}

	return (
		<Modal
			open={!!ticket}
			onCancel={onClose}
			footer={null}
			width={600}
			title={
				<div className="flex items-center gap-3">
					<div className="bg-indigo-500 rounded-lg p-2 flex items-center justify-center">
						<EyeOutlined className="text-white text-base" />
					</div>
					<div>
						<div className="text-lg font-semibold text-gray-800">Response Details</div>
						<div className="text-sm text-gray-500">
							{`${ticket.callerPhone} • ${formatDate(ticket.callDate)}`}
						</div>
					</div>
				</div>
			}
		>
			<div className="max-h-[70vh] overflow-y-auto pt-3">
				<div className="mb-6">
					{ticket.questions.map((question, qIdx) => (
						<div key={qIdx} className="mb-4">
							<div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
								{question}
							</div>
							<div className="text-sm text-gray-800 font-medium bg-gray-50 p-3 rounded-lg border border-gray-200 leading-relaxed">
								{ticket.answers[question] || '—'}
							</div>
						</div>
					))}
				</div>

				<div className="mb-6">
					<h4 className="text-base font-semibold text-gray-800 mb-4">Note</h4>
					<div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200 leading-relaxed min-h-[60px]">
						{ticket.note || 'No note added'}
					</div>
				</div>

				<div>
					<h4 className="text-base font-semibold text-gray-800 mb-4">Call Information</h4>
					<div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
						<div>
							<div className="text-xs text-gray-500 mb-1">Phone Number</div>
							<div className="text-sm font-medium text-gray-800">{ticket.callerPhone}</div>
						</div>
						<div>
							<div className="text-xs text-gray-500 mb-1">Call Date</div>
							<div className="text-sm font-medium text-gray-800">{formatDate(ticket.callDate)}</div>
						</div>
						<div>
							<div className="text-xs text-gray-500 mb-1">Status</div>
							<div
								className={`text-sm font-medium ${ticket.status === 'COMPLETED' ? 'text-green-600' : 'text-gray-600'}`}
							>
								{ticket.status}
							</div>
						</div>
						{ticket.completedAt && (
							<div>
								<div className="text-xs text-gray-500 mb-1">Completed Date</div>
								<div className="text-sm font-medium text-gray-800">
									{formatDate(ticket.completedAt)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};
